/** Property names and array indices in the document. */
export type EditorLocation = readonly (string | number)[];

/** How long to keep looking for a location before treating it as gone. */
const LOCATION_TIMEOUT_MS = 2000;

/**
 * Escape a value for a quoted attribute selector. CSS.escape is for identifiers: it renders an
 * array index as a numeric escape (`0` becomes `\\30 `), which not every selector engine decodes
 * inside a quoted string.
 */
const quote = (value: string) => value.replace(/["\\]/g, '\\$&');

/** Let queued renders run: a frame for the editor to paint, then a task for the work it deferred. */
const tick = () => new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));

/**
 * Content keeps mounting under what the last step opened, which moves the target, so let the
 * editor settle before scrolling. The delay matches the one findAndGoToComponent navigates with.
 */
const settle = async () => {
  await tick();
  await new Promise<void>(resolve => setTimeout(resolve, 100));
  await tick();
};

/**
 * A union renders its branch a tick after the element around it, and each nested union costs
 * another tick, so what a tab or expander click reveals is not in the DOM by the next paint.
 * Retry until the editor settles on something.
 */
async function waitFor<T extends Element>(find: () => T | null | undefined, describe: () => string): Promise<T> {
  const deadline = Date.now() + LOCATION_TIMEOUT_MS;
  for (;;) {
    const found = find();
    if (found) return found;
    if (Date.now() >= deadline) throw new Error(describe());
    await tick();
  }
}

/** Open the tab and collapsed properties, then scroll to the result. */
export async function goToEditorLocation(root: ShadowRoot | Element, path: EditorLocation): Promise<void> {
  const gone = (step: number, detail: string) =>
    `Search location no longer exists: ${JSON.stringify(path)} — step ${step} (${JSON.stringify(path[step])}): ${detail}`;
  let scope: Element | ShadowRoot = root;
  for (const [step, part] of Array.from(path.entries())) {
    // One value can be wrapped in several elements — a ref around a union around an intersection —
    // and they all carry its tracker id, so take the outermost and match on the id rather than on
    // a wrapper kind. An intersection renders its mergeable constraints as an object of their own
    // (`Commentable` alone is an empty one), so the first object here is often not the value's.
    const owner = await waitFor(
      () => scope.querySelector('[data-tracker-type]'),
      () => gone(step, 'nothing is rendered here'));
    const ownerId = owner.getAttribute('data-tracker-id');
    const owned = (selector: string) => Array.from(owner.querySelectorAll(selector))
      .find(element => element.getAttribute('data-editor-owner') === ownerId);
    if (typeof part === 'string') {
      const tab = owned(`[data-tab-name="${quote(part)}"]`);
      if (tab) (tab as HTMLElement).click();
    }
    const attribute = typeof part === 'number' ? 'data-editor-index' : 'data-editor-property';
    const child = await waitFor(
      () => owned(`[${attribute}="${quote(String(part))}"]`),
      () => gone(step, `${ownerId} renders ${list(owner, `[${attribute}]`, attribute, ownerId)}`));
    const expander = Array.from(child.querySelectorAll('[data-editor-expand]'))
      .find(button => button.closest('[data-editor-property]') === child);
    if (expander?.getAttribute('aria-expanded') === 'false') (expander as HTMLElement).click();
    scope = child;
  }
  await settle();
  if (scope instanceof Element) scope.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** The values the editor does offer at a step, so a failure says what it found instead. */
function list(scope: Element | ShadowRoot, selector: string, attribute: string, ownerId?: string | null): string {
  const values = Array.from(scope.querySelectorAll(selector))
    .filter(element => ownerId === undefined || element.getAttribute('data-editor-owner') === ownerId)
    .map(element => element.getAttribute(attribute));
  const unique = Array.from(new Set(values));
  if (!unique.length) return 'nothing';
  return unique.slice(0, 12).join(', ') + (unique.length > 12 ? ', …' : '');
}
